import * as React from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useRunInferenceMutation } from "../api/inferenceApi";
import { extractStructuredDocument, acceptEdit, rejectEdit } from "../../../utils/taskpane";
import { Message } from "../types/inference-request";
import ModelSelect from "./ModelSelect";
import { ChatModel } from "../types/model";
import { buildChatMessages } from "../utils/chatMessageBuilder";
import { parseCitations, enrichCitations } from "../utils/citationParser";
import { parseArgs } from "../utils/parseArgs";
import { executeToolCall } from "../utils/toolExecutor";
import { buildEditInfo, EditInfo } from "../utils/editTracker";
import ChatMessageBubble, { ChatMessage } from "./ChatMessageBubble";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { ArrowUpwardOutlined, StopRounded } from "@mui/icons-material";
import { StatusStep } from "../types/status-step";
import DevToolsMenu from "./DevTools";
import { useEffect, useRef, useState } from "react";

const MAX_TOOL_ROUNDS = 50;

const DraftChat = () => {
  const [selectedModel, setSelectedModel] = useState<ChatModel | null>(null);
  const [prompt, setPrompt] = useState("");
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isPreparingDocument, setIsPreparingDocument] = useState(false);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewContent, setPreviewContent] = useState("");
  const [conversationHistory, setConversationHistory] = useState<Message[]>([]);
  const [debugOpen, setDebugOpen] = useState(false);
  const [statusSteps, setStatusSteps] = useState<StatusStep[]>([]);
  const lastDocSnapshotRef = useRef<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const stoppedRef = useRef(false);
  const currentInferenceRef = useRef<{ abort: () => void } | null>(null);

  const [runInference, { isLoading }] = useRunInferenceMutation();

  const handleStop = () => {
    stoppedRef.current = true;
    currentInferenceRef.current?.abort();
  };

  const addStep = (label: string) => {
    setStatusSteps((prev) => [
      ...prev.map((s) => (s.status === "active" ? { ...s, status: "done" as const } : s)),
      { label, status: "active" as const },
    ]);
  };

  const markStepFailed = (error: string) => {
    setStatusSteps((prev) =>
      prev.map((s, i) =>
        i === prev.length - 1 && s.status === "active"
          ? { ...s, status: "failed" as const, error }
          : s
      )
    );
  };

  const clearSteps = () => setStatusSteps([]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, statusSteps]);

  const handleSend = async () => {
    if (!prompt.trim() || !selectedModel) {
      setError("Please select a model and enter a message.");
      return;
    }

    setError(null);
    stoppedRef.current = false;
    const userMessage: ChatMessage = {
      role: "user",
      content: prompt.trim(),
      timestamp: Date.now(),
    };
    setMessages((prev) => [...prev, userMessage]);
    setPrompt("");

    try {
      setIsPreparingDocument(true);
      clearSteps();
      addStep("Reading document");
      const docModel = await extractStructuredDocument();

      // Detect document edits since the last turn
      const currentSnapshot = JSON.stringify(docModel);
      let historyForRequest = conversationHistory;
      if (lastDocSnapshotRef.current !== null && currentSnapshot !== lastDocSnapshotRef.current) {
        historyForRequest = [
          ...conversationHistory,
          {
            role: "user",
            content:
              "Note: The document has been modified since your last response. " +
              "Segment IDs and content may have changed. Use only the current " +
              "document state provided above for all references — do not reuse " +
              "segment IDs from earlier tool calls.",
          },
        ];
      }
      lastDocSnapshotRef.current = currentSnapshot;

      const request = await buildChatMessages(userMessage.content, historyForRequest, docModel);
      setIsPreparingDocument(false);

      const turnMessages: Message[] = [{ role: "user", content: userMessage.content }];
      const turnEdits: EditInfo[] = [];
      let currentRequest = request;
      let hitToolLimit = false;
      let needsSummary = false;

      // Bookmark chain for sequential insert_paragraph "after" calls
      const lastInsertBookmarks = new Map<string, string>();

      for (let round = 0; round < MAX_TOOL_ROUNDS; round++) {
        if (stoppedRef.current) break;
        addStep(round === 0 ? "Sending message" : "Processing tool results");
        const inferenceCall = runInference({
          modelObjectIdentifier: selectedModel.objectIdentifier,
          request: currentRequest,
        });
        currentInferenceRef.current = inferenceCall;
        const response = await inferenceCall.unwrap();

        const choice = response.choices?.[0];
        if (!choice) break;

        const assistantMessage = choice.message;

        // ── Tool-call round ──────────────────────────────────────────────
        if (assistantMessage.tool_calls && assistantMessage.tool_calls.length > 0) {
          // Warn AI when approaching limit
          if (round === MAX_TOOL_ROUNDS - 3) {
            // Add context about approaching limit
            currentRequest = {
              ...currentRequest,
              messages: [
                ...currentRequest.messages,
                {
                  role: "user",
                  content:
                    "Notice: You have 3 more tool call rounds remaining. Consider planning your remaining actions efficiently.",
                },
              ],
            };
          } else if (round === MAX_TOOL_ROUNDS - 1) {
            currentRequest = {
              ...currentRequest,
              messages: [
                ...currentRequest.messages,
                {
                  role: "user",
                  content:
                    "Notice: This is your final tool call round. After this, you'll need to provide a summary of progress and indicate if continuation is needed.",
                },
              ],
            };
          }

          const assistantToolMsg: Message = {
            role: "assistant",
            content: assistantMessage.content,
            tool_calls: assistantMessage.tool_calls.map((tc) => ({
              id: tc.id,
              type: "function" as const,
              function: {
                name: tc.function.name,
                arguments:
                  typeof tc.function.arguments === "string"
                    ? tc.function.arguments
                    : JSON.stringify(tc.function.arguments),
              },
            })),
          };
          turnMessages.push(assistantToolMsg);

          const toolResultMessages: Message[] = [];
          for (const call of assistantMessage.tool_calls) {
            const toolLabel = call.function.name
              .replace(/_/g, " ")
              .replace(/\b\w/g, (c: string) => c.toUpperCase());
            addStep(toolLabel);

            // Inject bookmark chain for sequential inserts targeting the same position
            let effectiveArgs: object | string = call.function.arguments;
            if (call.function.name === "insert_paragraph") {
              const parsed = parseArgs<Record<string, unknown>>(call.function.arguments);
              const key = `${parsed.segmentId}:${parsed.position}`;
              if (parsed.position === "after" && lastInsertBookmarks.has(key)) {
                effectiveArgs = {
                  ...parsed,
                  insertAfterBookmark: lastInsertBookmarks.get(key),
                };
              } else {
                effectiveArgs = parsed;
              }
            } else if (call.function.name === "insert_chained_paragraphs") {
              const parsed = parseArgs<Record<string, unknown>>(call.function.arguments);
              const key = `${parsed.segmentId}:${parsed.position}`;
              if (lastInsertBookmarks.has(key)) {
                effectiveArgs = {
                  ...parsed,
                  insertAfterBookmark: lastInsertBookmarks.get(key),
                };
              } else {
                effectiveArgs = parsed;
              }
            } else if (call.function.name === "insert_table") {
              const parsed = parseArgs<Record<string, unknown>>(call.function.arguments);
              const key = `${parsed.segmentId}:${parsed.position}`;
              if (lastInsertBookmarks.has(key)) {
                // A prior insert in this batch already wrote to this position.
                // Remap to "after" the last bookmark so the table follows that
                // content rather than re-anchoring to the (now-stale) segment index.
                effectiveArgs = {
                  ...parsed,
                  position: "after",
                  insertAfterBookmark: lastInsertBookmarks.get(key),
                };
              } else {
                effectiveArgs = parsed;
              }
            }

            const result = await executeToolCall(call.function.name, effectiveArgs);
            if (!result.success) markStepFailed(result.error ?? "Unknown error");
            // Update bookmark chain for the next call in this batch
            if (result.success && result.bookmarkName) {
              if (
                call.function.name === "insert_paragraph" ||
                call.function.name === "insert_chained_paragraphs"
              ) {
                const parsed = parseArgs<Record<string, unknown>>(call.function.arguments);
                const key = `${parsed.segmentId}:${parsed.position}`;
                lastInsertBookmarks.set(key, result.bookmarkName);
              } else if (call.function.name === "insert_table") {
                // Key off the original (pre-remap) position so subsequent inserts
                // targeting the same logical slot continue to chain correctly.
                const parsed = parseArgs<Record<string, unknown>>(call.function.arguments);
                const key = `${parsed.segmentId}:${parsed.position}`;
                lastInsertBookmarks.set(key, result.bookmarkName);
              }
            }

            toolResultMessages.push({
              role: "tool",
              content: JSON.stringify(result),
              tool_call_id: call.id,
            });

            // Build edit card (single call replaces the old 9 if-blocks)
            const editResult = buildEditInfo(
              call.function.name,
              call.function.arguments,
              result,
              docModel
            );
            if (editResult) {
              if (Array.isArray(editResult)) turnEdits.push(...editResult);
              else turnEdits.push(editResult);
            }
          }
          turnMessages.push(...toolResultMessages);

          currentRequest = {
            ...currentRequest,
            messages: [...currentRequest.messages, assistantToolMsg, ...toolResultMessages],
          };

          if (choice.finish_reason === "stop") break;

          // Check if we're about to hit the limit
          if (round === MAX_TOOL_ROUNDS - 1) {
            hitToolLimit = true;
            needsSummary = true;
          }
          continue;
        }

        // ── Final text response ──────────────────────────────────────────
        const reply = assistantMessage.content?.trim();
        const assistantContent =
          reply && reply.length > 0 ? reply : "No response content returned.";

        const { content: cleanContent, citations } = parseCitations(assistantContent);
        await enrichCitations(citations, docModel);

        turnMessages.push({ role: "assistant", content: assistantContent });

        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content: cleanContent,
            edits: turnEdits.length > 0 ? turnEdits : undefined,
            citations: citations.length > 0 ? citations : undefined,
            model: selectedModel.name,
            timestamp: Date.now(),
          },
        ]);
        clearSteps();
        break;
      }

      // ── Handle tool limit reached ──────────────────────────────────────────
      if (hitToolLimit && needsSummary) {
        try {
          addStep("Generating summary");
          // Make a final call without tools to get summary
          const summaryRequest = {
            messages: [
              ...currentRequest.messages,
              {
                role: "system" as const,
                content:
                  "You have reached the tool call limit. Please provide a clear summary of what you accomplished and whether you would need additional tool rounds to complete the user's request. If more work is needed, offer to continue where you left off.",
              },
            ],
            // No tools array = tools disabled
          };

          const summaryResponse = await runInference({
            modelObjectIdentifier: selectedModel.objectIdentifier,
            request: summaryRequest,
          }).unwrap();

          const summaryContent =
            summaryResponse.choices?.[0]?.message?.content?.trim() ||
            "I've completed the available tool actions. Some work may remain incomplete due to the tool call limit.";

          const { content: cleanSummaryContent, citations: summaryCitations } =
            parseCitations(summaryContent);
          await enrichCitations(summaryCitations, docModel);

          turnMessages.push({ role: "assistant", content: summaryContent });

          setMessages((prev) => [
            ...prev,
            {
              role: "assistant",
              content: cleanSummaryContent,
              edits: turnEdits.length > 0 ? turnEdits : undefined,
              citations: summaryCitations.length > 0 ? summaryCitations : undefined,
              model: selectedModel.name,
              timestamp: Date.now(),
            },
          ]);
          clearSteps();
        } catch (summaryError) {
          console.error("Failed to generate summary:", summaryError);
          // Fallback message
          const fallbackContent =
            "I've completed multiple actions but reached the tool call limit. Some work may remain incomplete. Would you like me to continue where I left off?";
          turnMessages.push({ role: "assistant", content: fallbackContent });
          setMessages((prev) => [
            ...prev,
            {
              role: "assistant",
              content: fallbackContent,
              edits: turnEdits.length > 0 ? turnEdits : undefined,
              model: selectedModel.name,
              timestamp: Date.now(),
            },
          ]);
          clearSteps();
        }
      }

      setConversationHistory((prev) => [...prev, ...turnMessages]);

      if (turnEdits.length > 0) {
        const updatedDoc = await extractStructuredDocument();
        lastDocSnapshotRef.current = JSON.stringify(updatedDoc);
      }
    } catch (err) {
      setIsPreparingDocument(false);
      clearSteps();
      if (!stoppedRef.current) {
        setError(String(err));
      }
    }
  };

  const handleEditAction = async (
    messageIndex: number,
    editIndex: number,
    action: "accepted" | "rejected"
  ): Promise<boolean> => {
    const edit = messages[messageIndex]?.edits?.[editIndex];
    // Nothing to resolve is not a failure — the bulk handlers must not stop here.
    if (!edit?.bookmarkName || edit.status !== "pending") return true;

    const fn = action === "accepted" ? acceptEdit : rejectEdit;
    const result = await fn(edit.bookmarkName);

    // Only mark the edit resolved when Word confirms nothing is left pending in
    // its span. A partial resolution used to be reported as success, so the chat
    // showed the edit accepted while the redline was still in the document.
    // (Ticket 550)
    if (!result.success) {
      setError(result.error ?? `Could not ${action === "accepted" ? "accept" : "reject"} the change.`);
      return false;
    }

    setError(null);
    setMessages((prev) =>
      prev.map((msg, mi) =>
        mi === messageIndex && msg.edits
          ? {
              ...msg,
              edits: msg.edits.map((e, ei) => (ei === editIndex ? { ...e, status: action } : e)),
            }
          : msg
      )
    );
    return true;
  };

  const handleAcceptEdit = (mi: number, ei: number) => handleEditAction(mi, ei, "accepted");
  const handleRejectEdit = (mi: number, ei: number) => handleEditAction(mi, ei, "rejected");

  // Both bulk handlers stop at the first edit that could not be fully resolved,
  // leaving it and the rest pending so the error names a recoverable state
  // instead of scrolling past several silent partial resolutions. (Ticket 550)
  const handleAcceptAll = async (messageIndex: number) => {
    const edits = messages[messageIndex]?.edits;
    if (!edits) return;
    for (let i = 0; i < edits.length; i++) {
      if (edits[i].status === "pending" && edits[i].bookmarkName) {
        if (!(await handleAcceptEdit(messageIndex, i))) return;
      }
    }
  };

  const handleRejectAll = async (messageIndex: number) => {
    const edits = messages[messageIndex]?.edits;
    if (!edits) return;
    for (let i = edits.length - 1; i >= 0; i--) {
      if (edits[i].status === "pending" && edits[i].bookmarkName) {
        if (!(await handleRejectEdit(messageIndex, i))) return;
      }
    }
  };

  const handlePreview = async () => {
    setError(null);
    setIsPreviewLoading(true);
    try {
      const doc = await extractStructuredDocument();
      setPreviewContent(JSON.stringify(doc, null, 2));
      setPreviewOpen(true);
    } catch {
      setError("Failed to prepare document preview.");
    } finally {
      setIsPreviewLoading(false);
    }
  };

  const isSending = isLoading || isPreparingDocument;

  const handleNewChat = () => {
    setMessages([]);
    setConversationHistory([]);
    setError(null);
    clearSteps();
  };

  return (
    <Stack sx={{ height: "100%", overflow: "hidden", boxSizing: "border-box" }}>
      <Box sx={{ flex: 1, overflowY: "auto" }}>
        <Stack spacing={1} p={1}>
          {messages.length === 0 ? (
            <Typography
              variant="body1"
              color="text.secondary"
              textAlign="center"
              sx={{ pt: 4, px: 2 }}
            >
              What's on the agenda today? Ask me to read, edit, or analyse your document, or just
              have a chat!
            </Typography>
          ) : (
            messages.map((message, index) => (
              <ChatMessageBubble
                key={`msg-${index}`}
                message={message}
                messageIndex={index}
                onAcceptEdit={handleAcceptEdit}
                onRejectEdit={handleRejectEdit}
                onAcceptAll={handleAcceptAll}
                onRejectAll={handleRejectAll}
              />
            ))
          )}
          <div ref={messagesEndRef} />
          {statusSteps.length > 0 && (
            <Paper variant="outlined" sx={{ p: 1.5, bgcolor: "background.paper" }}>
              <Stack spacing={0.5}>
                {statusSteps.map((step, i) => (
                  <Stack key={i} direction="row" alignItems="center" spacing={1}>
                    {step.status === "done" ? (
                      <CheckCircleIcon sx={{ fontSize: 16, color: "success.main" }} />
                    ) : step.status === "failed" ? (
                      <Tooltip title={step.error ?? "Failed"} placement="right">
                        <ErrorOutlineIcon
                          sx={{ fontSize: 16, color: "error.main", cursor: "default" }}
                        />
                      </Tooltip>
                    ) : (
                      <CircularProgress size={14} />
                    )}
                    <Box>
                      <Typography
                        variant="caption"
                        color={
                          step.status === "failed"
                            ? "error.main"
                            : step.status === "done"
                              ? "text.secondary"
                              : "text.primary"
                        }
                        sx={{ fontWeight: step.status === "active" ? 600 : 400, display: "block" }}
                      >
                        {step.label}
                      </Typography>
                    </Box>
                  </Stack>
                ))}
              </Stack>
            </Paper>
          )}
        </Stack>
      </Box>

      {error && <Alert severity="error">{error}</Alert>}

      {/* Input */}
      <Box p={2}>
        <Box
          sx={{
            border: 1,
            borderColor: "divider",
            borderRadius: 1,
            padding: 1,
            backgroundColor: "background.paper",
            maxHeight: "60vh",
            overflow: "auto",
            "&:focus-within": {
              borderColor: "primary.main",
            },
          }}
        >
          <TextField
            placeholder="Type a message..."
            value={prompt}
            onChange={(event) => setPrompt(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter" && !event.shiftKey) {
                event.preventDefault();
                if (prompt.trim() && selectedModel && !isSending) {
                  handleSend();
                }
              }
            }}
            fullWidth
            multiline
            minRows={2}
            size="small"
            variant="standard"
            slotProps={{
              input: {
                disableUnderline: true,
              },
            }}
            sx={{ mb: 1 }}
          />
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Box sx={{ "& .MuiOutlinedInput-notchedOutline": { border: "none" } }}>
                <ModelSelect selectedModel={selectedModel} onChange={setSelectedModel} />
              </Box>
              <DevToolsMenu
                onPreview={handlePreview}
                isPreviewLoading={isPreviewLoading || isPreparingDocument}
                onRawHistory={() => setDebugOpen(true)}
                hasHistory={conversationHistory.length > 0}
              />
              <Button
                variant="text"
                size="small"
                onClick={handleNewChat}
                disabled={isSending || (messages.length === 0 && conversationHistory.length === 0)}
              >
                Reset Chat
              </Button>
            </Stack>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              {isSending ? (
                <IconButton onClick={handleStop} color="error" title="Cancel">
                  <StopRounded fontSize="small" />
                </IconButton>
              ) : (
                <IconButton
                  onClick={handleSend}
                  disabled={!prompt.trim() || !selectedModel}
                  sx={{
                    color: !prompt.trim() || !selectedModel ? "text.disabled" : "primary.main",
                  }}
                >
                  <ArrowUpwardOutlined fontSize="small" />
                </IconButton>
              )}
            </Stack>
          </Stack>
        </Box>
      </Box>

      {/* Dialogs */}
      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Document Preview</DialogTitle>
        <DialogContent>
          <TextField
            value={previewContent}
            multiline
            minRows={12}
            fullWidth
            slotProps={{ input: { readOnly: true } }}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={debugOpen} onClose={() => setDebugOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Raw Conversation History</DialogTitle>
        <DialogContent>
          <TextField
            value={JSON.stringify(conversationHistory, null, 2)}
            multiline
            minRows={12}
            fullWidth
            slotProps={{
              input: { readOnly: true, style: { fontFamily: "monospace", fontSize: 12 } },
            }}
          />
        </DialogContent>
      </Dialog>
    </Stack>
  );
};

export default DraftChat;
